import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { organizacionAPI } from '../services/api';

export default function Configuracion() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data: org, isLoading } = useQuery({
    queryKey: ['organizacion'],
    queryFn: async () => {
      const res = await organizacionAPI.obtener();
      return res.data.data;
    },
  });

  const subirMutation = useMutation({
    mutationFn: organizacionAPI.subirLogo,
    onSuccess: () => {
      queryClient.invalidateQueries(['organizacion']);
      setPreview(null);
      showSnackbar('Logo actualizado exitosamente', 'success');
    },
    onError: (err) => {
      showSnackbar(err.response?.data?.error?.message || 'Error al subir el logo', 'error');
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: organizacionAPI.eliminarLogo,
    onSuccess: () => {
      queryClient.invalidateQueries(['organizacion']);
      setPreview(null);
      showSnackbar('Logo eliminado', 'success');
    },
    onError: () => showSnackbar('Error al eliminar el logo', 'error'),
  });

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview({ file, url });
  };

  const handleGuardar = () => {
    if (preview?.file) subirMutation.mutate(preview.file);
  };

  const handleCancelar = () => {
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // URL con cache-buster para forzar recarga tras cambio
  const logoSrc = preview?.url ?? (org?.Logo ? `${org.Logo}?t=${Date.now()}` : null);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SettingsIcon sx={{ fontSize: 32 }} />
        <Typography variant="h4">Configuración</Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3, maxWidth: 560 }}>
          <Typography variant="h6" gutterBottom>
            {org?.NombreOrganizacion}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Logo de la organización
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se muestra en la barra superior de la aplicación. PNG o JPG, máximo 2 MB.
          </Typography>

          {/* Vista previa */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              src={logoSrc ?? undefined}
              variant="rounded"
              sx={{ width: 96, height: 96, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}
            >
              {!logoSrc && (
                <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
                  Sin logo
                </Typography>
              )}
            </Avatar>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                size="small"
              >
                {org?.Logo || preview ? 'Cambiar logo' : 'Subir logo'}
              </Button>

              {org?.Logo && !preview && (
                <Tooltip title="Eliminar logo actual">
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      size="small"
                      onClick={() => eliminarMutation.mutate()}
                      disabled={eliminarMutation.isPending}
                    >
                      Eliminar
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Botones de confirmación cuando hay preview */}
          {preview && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleGuardar}
                disabled={subirMutation.isPending}
              >
                {subirMutation.isPending ? 'Guardando...' : 'Guardar logo'}
              </Button>
              <Button variant="outlined" onClick={handleCancelar}>
                Cancelar
              </Button>
            </Box>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
